import type { Availability } from '@/types';

import dayjs from 'dayjs';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import widgetSettings from '@/widget-settings';
import { loadAvailabilities } from './load-availability';

export const DEFAULT_RANGE = 14;

export const useAvailabilityStore = defineStore('availability', () => {
  const { settings, waitinglistEnabled } = useCompanyStore();

  const availabilities = ref<Availability[]>([]);
  const offset = ref(0);
  const loadingAvailability = ref(false);
  const availabilitiesToday = computed(() => {
    const { date } = useBookingStore();
    return availabilities.value.find((a) => a.date === date);
  });

  const resetAvailabilityStore = () => {
    availabilities.value = [];
    offset.value = 0;
    loadingAvailability.value = false;
  };

  const isAllDataLoaded = computed(() => {
    return settings.last_bookable_day
      ? availabilities.value
          .map((item) => item.date)
          .includes(settings.last_bookable_day)
      : false;
  });

  const selectFirstAvailableDate = (data: Availability[]) => {
    let firstAvailableDate: any;
    if (waitinglistEnabled) {
      firstAvailableDate = data.find((item) => item.open);
    } else {
      firstAvailableDate = data.find((item) => item.timeslots.length > 0);
    }
    if (firstAvailableDate) {
      const { date } = storeToRefs(useBookingStore());
      date.value = firstAvailableDate.date;
    }
  };

  const addAvailabities = (data: Availability[]) => {
    if (availabilities.value.length === 0) {
      selectFirstAvailableDate(data);
    }

    availabilities.value = availabilities.value.concat(data);
  };

  const addEmptyDates = (firstDate: string) => {
    if (!availabilities.value || !availabilities.value.length) {
      return;
    }

    const dateLast = dayjs(
      availabilities.value[availabilities.value.length - 1].date
    );
    const newDates = [];

    let range = 0;
    let diffDays = 0;

    if (firstDate) {
      const dateFirst = dayjs(firstDate);
      diffDays = dateFirst.diff(dateLast, 'day');

      range += diffDays;
      offset.value += range - 1;
    } else {
      offset.value += range;
    }

    for (let i = 0; i < range - 1; i++) {
      const date = dateLast.add(i + 1, 'day');
      newDates.push(getEmptyAvailability(date.format('YYYY-MM-DD')));
    }

    addAvailabities(newDates);
  };

  const getEmptyAvailability: (date: string) => Availability = (date) => {
    return {
      date,
      timeslots: [],
      open: false,
      open_resources: [],
      available_resources: []
    };
  };

  const hasAvailability = (date: string) =>
    availabilities.value.find((availability) => availability.date === date);

  const getAvailability: (date: string) => Availability = (date) => {
    const availability = availabilities.value.find(
      (availability) => availability.date === date
    );
    return availability ? availability : getEmptyAvailability(date);
  };

  const limitToLastBookableDay = (amountOfDays: number) => {
    const lastLoadedDate =
      availabilities.value.length > 0
        ? dayjs(availabilities.value[availabilities.value.length - 1].date)
        : dayjs();
    const lastBookableDay = dayjs(settings.last_bookable_day);

    return Math.min(lastBookableDay.diff(lastLoadedDate, 'day'), amountOfDays);
  };

  const fetchAvailabilities = (range = DEFAULT_RANGE) => {
    const { locationId, serviceIds } = useBookingStore();

    if (!locationId) {
      return new Promise((_resolve, reject) =>
        reject(new Error('Location ID is not defined.'))
      );
    }

    // Overfetch on initial call for reschedule flow to have higher chance of hitting an available date
    if (widgetSettings.bookingId && !availabilities.value?.length) {
      range = 31;
    }

    if (!availabilities.value?.length && settings.overload_availability) {
      range = 90;
    }

    const maxRange = limitToLastBookableDay(range);
    if (maxRange <= 0) {
      return Promise.resolve();
    }

    return loadAvailabilities({
      locationId: locationId,
      serviceIds: serviceIds,
      bookingId: widgetSettings.bookingId,
      offset: offset.value,
      range: maxRange,
      widgetId: widgetSettings.widgetId
    }).then((data) => {
      if (data) {
        addAvailabities(data);
        offset.value += maxRange;
      }
    });
  };

  return {
    availabilities,
    availabilitiesToday,
    loadingAvailability,
    isAllDataLoaded,
    getAvailability,
    hasAvailability,
    getEmptyAvailability,
    addEmptyDates,
    fetchAvailabilities,
    resetAvailabilityStore
  };
});
